import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { message } from 'antd';
import { d201Api, FD110, FD111 } from '../../../services/d201Api';
import { useFieldErrors } from '../../../hooks/useFieldErrors';

export type Mode = 'browse' | 'add' | 'edit';

const PURCHASE_TYPE_OPTIONS = [
  { value: '1', label: '原料' },
  { value: '2', label: '半成品' },
  { value: '3', label: '成品' },
];

export function usePurchaseForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FD110[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<FD110>>({});
  const [details, setDetails] = useState<Partial<FD111>[]>([]);
  const [lookupNames, setLookupNames] = useState<Record<string, string>>({});
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);
  const { fieldErrors, setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Load records list
  const loadRecords = useCallback(async (keyword?: string) => {
    setLoading(true);
    try {
      const res = await d201Api.list(1, 500, keyword);
      setLastApiResponse(res);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Resolve lookup names for display
  const resolveNames = useCallback(async (data: Partial<FD110>) => {
    const entries: [string, string, string][] = [
      ['supplier', 'customer', data.fd110_12 || ''],
      ['department', 'department', data.fd110_18 || ''],
      ['salesperson', 'employee', data.fd110_17 || ''],
      ['currency', 'currency', data.fd110_19 || ''],
      ['paymentTerms', 'paymentTerms', data.fd110_151 || ''],
      ['tradeTerms', 'tradeTerms', data.fd110_141 || ''],
      ['packing', 'packing', data.fd110_16 || ''],
      ['qcPerson', 'employee', data.fd110_30 || ''],
      ['purchaser', 'employee', data.fd110_31 || ''],
      ['receiverCompany', 'customer', data.fd110_33 || ''],
      ['entryPerson', 'employee', data.fd110_91 || ''],
    ];
    const results = await Promise.all(
      entries.map(([key, type, code]) =>
        d201Api.lookupName(type, code).then(name => [key, name] as [string, string])
      )
    );
    setLookupNames(prev => {
      const next = { ...prev };
      for (const [key, name] of results) next[key] = name;
      return next;
    });
  }, []);

  const resolveSingleName = useCallback(async (nameKey: string, type: string, code: string) => {
    const name = await d201Api.lookupName(type, code);
    setLookupNames(prev => ({ ...prev, [nameKey]: name }));
  }, []);

  // Sync formData when current changes in browse mode
  useEffect(() => {
    if (current && mode === 'browse') {
      setFormData({ ...current });
      setDetails(current.details || []);
      resolveNames(current);
    }
  }, [current, mode, resolveNames]);

  // Initial load + handle ?review= param from approval list
  const [searchParams] = useSearchParams();
  useEffect(() => {
    loadRecords().then(data => {
      const reviewNo = searchParams.get('review');
      if (reviewNo) {
        const idx = data.findIndex(r => r.fd110_01 === reviewNo);
        setCurrentIdx(idx >= 0 ? idx : 0);
      } else if (data.length > 0) {
        setCurrentIdx(0);
      }
    });
  }, [loadRecords, searchParams]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = useCallback(() => {
    clearFieldErrors();
    setCurrentIdx(-1); // Clear current record so status tag resets
    setMode('add');
    setFormData({
      fd110_19: 'NTD',
      fd110_20: 1,
      fd110_28: '1',
      fd110_29: 'N',
      fd110_46: '1',  // 原料(預設)
      fd110_80: '',
    });
    setDetails([{ fd111_02: 1 }]);
    setLookupNames({});
  }, [clearFieldErrors]);

  // Start edit mode
  const startEdit = useCallback(() => {
    if (!current) return;
    if (current.fd110_80 === 'P') {
      message.warning('已核准的單據不可修改');
      return;
    }
    setMode('edit');
    setFormData({ ...current });
    setDetails(current.details || []);
  }, [current]);

  // Cancel editing
  const cancel = useCallback(() => {
    clearFieldErrors();
    setMode('browse');
    if (current) {
      setFormData({ ...current });
      setDetails(current.details || []);
    }
  }, [current, clearFieldErrors]);

  // Save (create or update)
  const save = useCallback(async () => {
    try {
      setLoading(true);
      const req = {
        master: formData as FD110,
        details: details as FD111[],
      };
      if (mode === 'add') {
        const created = await d201Api.create(req);
        message.success(`新增成功: ${created.fd110_01}`);
        const data = await loadRecords();
        setLastApiResponse(created);
        const idx = data.findIndex(r => r.fd110_01 === created.fd110_01);
        setCurrentIdx(idx >= 0 ? idx : 0);
      } else if (current) {
        const updated = await d201Api.update(current.fd110_01, req);
        message.success('修改成功');
        const data = await loadRecords();
        setLastApiResponse(updated);
        const idx = data.findIndex(r => r.fd110_01 === updated.fd110_01);
        setCurrentIdx(idx >= 0 ? idx : 0);
      }
      setMode('browse');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        const summary = errors.slice(0, 5).map((e: { message: string }) => e.message).join('\n');
        const extra = errors.length > 5 ? `\n...共 ${errors.length} 項` : '';
        message.error({ content: summary + extra, duration: 6, style: { whiteSpace: 'pre-line' } });
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, mode, current, loadRecords, setFieldErrors]);

  // Delete
  const doDelete = useCallback(async () => {
    if (!current) return;
    try {
      await d201Api.delete(current.fd110_01);
      setLastApiResponse({ deleted: current.fd110_01 });
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  }, [current, loadRecords, currentIdx]);

  // Update a field
  const updateField = useCallback((field: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Update a detail row field
  const updateDetailField = useCallback((rowIdx: number, field: string, value: unknown) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[rowIdx]) {
        next[rowIdx] = { ...next[rowIdx], [field]: value };
      }
      return next;
    });
  }, []);

  // Add detail row
  const addDetailRow = useCallback(() => {
    const maxSeq = details.reduce((max, d) => Math.max(max, d.fd111_02 || 0), 0);
    setDetails(prev => [...prev, { fd111_02: maxSeq + 1, fd111_01: formData.fd110_01 || '' }]);
  }, [details, formData.fd110_01]);

  // Delete detail row
  const deleteDetailRow = useCallback((rowIdx: number) => {
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== rowIdx);
      return next.map((d, i) => ({ ...d, fd111_02: i + 1 }));
    });
  }, []);

  // SC import modal state
  const [showSCModal, setShowSCModal] = useState(false);

  // Import from SC: call preview API and fill form state
  const importFromSC = useCallback(async (scNo: string) => {
    try {
      setLoading(true);
      const preview = await d201Api.previewFromSC(scNo);
      setLastApiResponse(preview);
      setFormData(prev => ({
        ...prev,
        fd110_10: preview.master.fd110_10 || '',
        // fd110_12 (分包商) NOT auto-filled — customer ≠ supplier
        fd110_141: preview.master.fd110_141 || prev.fd110_141 || '',
        fd110_151: preview.master.fd110_151 || prev.fd110_151 || '',
        fd110_16: preview.master.fd110_16 || prev.fd110_16 || '',
        fd110_17: preview.master.fd110_17 || '',
        fd110_18: preview.master.fd110_18 || '',
        fd110_19: preview.master.fd110_19 || 'NTD',
        fd110_20: preview.master.fd110_20 ?? 1,
        fd110_46: prev.fd110_46 || '1',  // 原料/半成品/成品
        fd110_80: '',  // 確保新建時是草稿狀態
      }));
      setDetails(preview.details || []);
      // Resolve lookup names for newly filled fields
      if (preview.master) {
        resolveNames({ ...formData, ...preview.master } as Partial<FD110>);
      }
      setShowSCModal(false);
      message.success(`已從 S/C ${scNo} 帶入 ${(preview.details || []).length} 筆明細`);
    } catch {
      message.error('從 S/C 帶入失敗');
    } finally {
      setLoading(false);
    }
  }, [formData, resolveNames]);

  // Submit for approval
  const handleSubmit = useCallback(async (): Promise<boolean> => {
    if (!current) return false;
    try {
      const res = await d201Api.submit(current.fd110_01);
      message.success(res.message || '送審成功');
      const data = await loadRecords();
      setLastApiResponse(res);
      const idx = data.findIndex(r => r.fd110_01 === current.fd110_01);
      setCurrentIdx(idx >= 0 ? idx : 0);
      return true;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '送審失敗');
      return false;
    }
  }, [current, loadRecords]);

  // Approve current PO
  const handleApprove = useCallback(async (): Promise<boolean> => {
    if (!current) return false;
    try {
      const res = await d201Api.approve(current.fd110_01);
      message.success(res.message || '核准成功');
      const data = await loadRecords();
      setLastApiResponse(res);
      const idx = data.findIndex(r => r.fd110_01 === current.fd110_01);
      setCurrentIdx(idx >= 0 ? idx : 0);
      return true;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '核准失敗');
      return false;
    }
  }, [current, loadRecords]);

  // Reject current PO
  const handleReject = useCallback(async (): Promise<boolean> => {
    if (!current) return false;
    try {
      const res = await d201Api.reject(current.fd110_01);
      message.success(res.message || '駁回成功');
      const data = await loadRecords();
      setLastApiResponse(res);
      const idx = data.findIndex(r => r.fd110_01 === current.fd110_01);
      setCurrentIdx(idx >= 0 ? idx : 0);
      return true;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '駁回失敗');
      return false;
    }
  }, [current, loadRecords]);

  // Status label
  const statusLabel = useCallback((status: string): string => {
    switch (status) {
      case 'D': return '送審中';
      case 'P': return '已核准';
      case 'N': return '已沖銷';
      default: return '草稿';
    }
  }, []);

  // Computed: NTD total
  const ntdTotal = (formData.fd110_23 || 0) * (formData.fd110_20 || 1);

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    formData, details, lookupNames,
    updateField, updateDetailField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, loadRecords, statusLabel,
    addDetailRow, deleteDetailRow,
    resolveSingleName,
    ntdTotal,
    PURCHASE_TYPE_OPTIONS,
    showSCModal, setShowSCModal, importFromSC,
    fieldErrors, getFieldError, clearFieldErrors, hasErrors,
    handleSubmit, handleApprove, handleReject,
    lastApiResponse, setLastApiResponse,
  };
}
